// Shared contract between the Worker (src/worker) and the web app (web/src).
// Owned by the spec. Agents add module-specific types in src/shared/api-<module>.ts, never here.
// Every JSON response is either the documented body or ApiError with a non-2xx status.

import type { DialTarget, QueueItemNumbers } from './api-numbers';

export type ISODate = string; // ISO-8601 UTC, e.g. '2026-09-24T14:05:00.000Z'
export type Country = 'US' | 'CA';

export interface ApiError {
  error: string;          // machine code, e.g. 'gate_blocked', 'not_found', 'forbidden', 'validation'
  message: string;        // human sentence, safe to show a setter
  details?: unknown;
}

// ---------- auth ----------

export type Role = 'admin' | 'setter';

export interface Me {
  id: number;
  email: string;
  name: string;
  role: Role;
  twilioIdentity: string;
  certifyExempt: boolean;
  authMode: 'access' | 'password' | 'dev';
}

// ---------- dispositions ----------

export const DISPOSITIONS = [
  'no_answer',            // rang out, busy, or IVR dead end
  'vm_live',              // setter read a voicemail live
  'vm_dropped',           // set only by the drop endpoint
  'gatekeeper_intel',     // gatekeeper gave name / best time
  'gatekeeper_blocked',
  'dm_not_interested',    // decision-maker, dismissive
  'dm_later',             // decision-maker, situational ("not now") -> callback task
  'dm_existing_solution', // decision-maker, already has something
  'callback_set',         // agreed time to call back -> callback task (callbackAt required)
  'meeting_booked',       // requires a meeting row (booked via /api/meetings)
  'wrong_number',
  'not_a_fit',            // chain/MSO, ServiceTitan shop, closed, etc.
  'dnc_request',          // asked not to be called: DNC immediately, every campaign
] as const;
export type DispositionCode = (typeof DISPOSITIONS)[number];

/** "Call back now" (owner request, PJ, 2026-10-06) is for a call that didn't get through or was cut off: one still owed,
 *  or logged as one of these. The rest close, rest or schedule the lead, so a redial right away would contradict them.
 *  The dial gate (compliance/gates.ts quickCallbackProblem) and the dialer (web labels.ts) use this one list. */
export const QUICK_CALLBACK_DISPOSITIONS: readonly DispositionCode[] = ['no_answer', 'vm_live', 'gatekeeper_intel', 'gatekeeper_blocked'];

// Human-answered dispositions (count as a connect in reports).
export const CONNECT_DISPOSITIONS: readonly DispositionCode[] = [
  'gatekeeper_intel', 'gatekeeper_blocked', 'dm_not_interested', 'dm_later', 'dm_existing_solution',
  'callback_set', 'meeting_booked', 'not_a_fit', 'dnc_request',
];

export const OBJECTIONS = [
  'no_time', 'not_interested', 'send_info', 'have_someone', 'too_expensive', 'bad_experience',
  'ai_skeptic', 'need_partner', 'call_later', 'other',
] as const;
export type ObjectionCategory = (typeof OBJECTIONS)[number];

// ---------- campaigns ----------

export interface ObjectionCard {
  key: string;                 // 'too_expensive'
  label: string;               // button text, <= 24 chars
  category: ObjectionCategory;
  response: string;            // what to say, 1-3 short sentences
  followUpQuestion?: string;
}

export interface ScriptBlock { label: string; text: string }

export interface VoicemailScript {
  situation: string;           // 'first' | 'second' | 'no_show' | ...
  label: string;
  text: string;                // <= 25 s spoken; opens with name + "Valistix"; ends with a callback number placeholder {callback}
}

export interface EmailTemplate {
  situation: string;           // 'after_vm' | 'after_call' | ...
  subject: string;
  body: string;                // placeholders: {first_name} {business} {setter} {observation} {calendly_link}
}

export interface CampaignScript {
  summary: string;                     // one line: who we call and why
  openers: ScriptBlock[];              // variants; {setter} {business} {dm_name} {observation} placeholders
  reason: string;                      // reason for the call, said in the first ~15 s
  questions: ScriptBlock[];            // problem questions
  pitch: string;                       // one or two sentences, problem language
  ask: string;                         // meeting ask with two concrete times (next-day first)
  gatekeeper: ScriptBlock[];
  objections: ObjectionCard[];
  voicemailsLive: VoicemailScript[];   // read live by the setter
  voicemailsDrop: VoicemailScript[];   // scripts setters record for gated drops
  emails: EmailTemplate[];
  disqualifiers: string[];             // "not a fit" signals
  complianceNotes: string[];           // shown on the dialer: what this campaign must never say or do
}

export interface PreferredWindow {
  days: number[];              // 1 = Monday ... 6 = Saturday (0 = Sunday never dialled)
  start: string;               // 'HH:MM' prospect-local
  end: string;
  label: string;               // 'Owner before dispatch'
}

export type CampaignStatus = 'draft' | 'live' | 'paused' | 'done';

export interface Campaign {
  id: number;
  name: string;
  brand: string;
  niche: string;
  offer: string;
  country: Country | 'MIXED';
  geoLabel: string | null;
  weekStart: string | null;    // 'YYYY-MM-DD'
  status: CampaignStatus;
  maxAttempts: number;
  maxVms: number;
  calendlyEventSlug: string;
  script: CampaignScript;
  windows: PreferredWindow[];
  templateKey: string | null;
  clonedFrom: number | null;
  userIds: number[];           // setters assigned
  counts?: { total: number; new: number; inProgress: number; callback: number; meeting: number; closed: number };
  createdAt: ISODate;
  updatedAt: ISODate;
}

export interface CampaignTemplate {
  key: string;
  name: string;
  niche: string;
  offer: string;
  country: Country | 'MIXED';
  script: CampaignScript;
  windows: PreferredWindow[];
  personas: Array<{ kind: PersonaKind; name: string; brief: string }>;
  calendlyEventSlug?: string;  // default 'your-result' (setter link); dispensary templates use 'your-store'; the Book panel also offers the other non-cannabis setter link (growth-call)
}

// ---------- businesses ----------

export type LineType =
  | 'landline' | 'mobile' | 'fixedVoip' | 'nonFixedVoip' | 'personal' | 'tollFree' | 'premium'
  | 'sharedCost' | 'uan' | 'voicemail' | 'pager' | 'unknown';

export interface BusinessBrief {
  observation: string | null;          // one verifiable fact, or null if nothing verifiable was found
  observationSourceUrl: string | null;
  openerLine: string | null;           // suggested context opener using the observation
  likelyPain: string | null;
  fitFlags: string[];                  // e.g. 'looks like a chain (12 locations)'
  facts: Array<{ fact: string; sourceUrl: string }>;
  model: string;
  generatedAt: ISODate;
}

export interface Business {
  id: number;
  name: string;
  niche: string | null;
  phoneE164: string;
  phoneDisplay: string;
  phoneExt: string | null;
  country: Country;
  stateProv: string;
  city: string | null;
  tz: string;
  website: string | null;
  gbpUrl: string | null;
  email: string | null;
  emailSourceUrl: string | null;
  dmName: string | null;
  dmRole: string | null;
  dmSourceUrl: string | null;
  gatekeeperName: string | null;
  bestTimeNote: string | null;
  sourceUrl: string;
  lineType: LineType | null;
  lookupAt: ISODate | null;
  nationalDnc: boolean | null;
  dncScrubbedAt: ISODate | null;
  isHealthcare: boolean;
  doNotContact: boolean;
  badNumber: boolean;
  callerNumberId: number | null;
  observation: string | null;
  observationSourceUrl: string | null;
  observationVerifiedAt: ISODate | null;
  brief: BusinessBrief | null;
}

export type MembershipStatus =
  | 'new' | 'in_progress' | 'callback' | 'meeting' | 'closed_lost' | 'disqualified' | 'bad_data' | 'dnc' | 'exhausted';

export interface Membership {
  id: number;
  campaignId: number;
  businessId: number;
  status: MembershipStatus;
  ownerUserId: number | null;
  attempts: number;
  vmCount: number;
  lastAttemptAt: ISODate | null;
  lastWindow: LocalWindow | null;
  nextEligibleAt: ISODate | null;
  closedReason: string | null;
  /** Migration 0018: this lead's attempt cap (absolute count). null/absent = the campaign's maxAttempts. */
  attemptCap?: number | null;
  /** Migration 0018: the lead's latest recycle round, if it ever had one (SPEC §19 lead recycling). */
  recycle?: RecycleRound | null;
}

/** SPEC §19 (2026-10-02) lead recycling. 'exhausted': the one extra round after a 30-day rest (no decision-maker reached
 *  in the earlier attempts); 'has_one': the fresh round 6 months after "has one already". */
export interface RecycleRound {
  kind: 'exhausted' | 'has_one';
  recycledAt: ISODate;
  /** Attempts this round allows. */
  roundSize: number;
  /** Attempts placed in this round so far. */
  used: number;
}

/** "Recycle round: 1 of 3": the attempt this lead is on within its recycle round (the next one while attempts remain). */
export function recycleRoundLabel(r: RecycleRound): string {
  const size = Math.max(0, r.roundSize);
  if (r.used >= size) return `Recycle round: ${size} of ${size} (used up)`;
  return `Recycle round: ${Math.max(0, r.used) + 1} of ${size}`;
}

/** Hover text for the recycle chip. */
export function recycleRoundTitle(r: RecycleRound): string {
  return r.kind === 'exhausted'
    ? 'This lead used every attempt without reaching a decision-maker, rested, and is back for one extra round. After this round it is out of attempts for good.'
    : 'They said they already had one. This lead rested (6 months by default) and is back for a fresh round.';
}

export type LocalWindow = 'early' | 'mid' | 'late'; // before 11:00, 11:00-15:00, after 15:00 prospect-local

// ---------- compliance gates ----------

export type GateReasonCode =
  | 'dnc_internal' | 'do_not_contact' | 'bad_number' | 'healthcare_ca' | 'state_blocked' | 'state_unreviewed'
  | 'country_unsupported' | 'outside_window' | 'sunday' | 'blocked_date' | 'attempts_24h' | 'attempt_spacing'
  | 'attempts_week' | 'attempts_max' | 'national_dnc' | 'needs_dnc_scrub' | 'not_certified' | 'not_assigned'
  | 'campaign_not_live' | 'membership_closed' | 'locked_by_other' | 'no_caller_number'
  | 'state_line_type'    // the state allows calls only to verified business landlines / fixed VoIP (e.g. NJ, TN)
  | 'not_interested_cooldown' // 2026-10-01: a decision-maker said not interested; the business rests 90 days, every campaign
  | 'attempts_day_limit' // PJ 2026-10-20: 3 placed calls to this number in 24 h (any setter, any campaign); a hard stop, never confirmable
  // voicemail-drop-only reasons
  | 'drop_country' | 'drop_line_type' | 'drop_lookup_stale' | 'drop_state' | 'drop_toll_free' | 'drop_vm_limit'
  | 'drop_no_recording' | 'drop_recording_unapproved' | 'drop_not_answered';

export interface GateReason {
  code: GateReasonCode;
  message: string;             // human sentence for the setter, e.g. "It's 7:42 pm in Denver; calling closes at 8:00 pm"
}

export interface GateResult {
  ok: boolean;
  reasons: GateReason[];       // empty when ok
  retryAt?: ISODate;           // earliest time this could pass (window/spacing reasons)
  /** PJ 2026-10-20 ("It should be just warnings on dial backs saying that, hey, you have called this back. Do you still
   *  want to dial it?"): set only when ok is false and EVERY failing reason is a soft attempt rule (4 h spacing, 2 a day,
   *  3 a week, the campaign's attempt cap, a lead out of attempts) or (PJ 2026-10-28) a meeting booked. The dialer shows
   *  an amber warning and asks; a yes is CallPrepareRequest.redialConfirmed. Absent = a hard block, never confirmable. */
  confirm?: GateConfirm;
}

/** What the "Call this number again?" dialog says: one plain sentence ending in "Call again?", and the soft reasons. */
export interface GateConfirm {
  prompt: string;
  reasons: GateReason[];
  /** PJ 2026-10-28 ("just give it like a warning that this is a meeting booked before he confirms the dial"): the lead
   *  has a meeting booked. The prompt starts with the meeting and ends "Call anyway?"; the dialog and the lead card say
   *  "Meeting booked". Its yes is CallPrepareRequest.meetingConfirmed. Absent = no meeting. */
  meeting?: true;
}

// ---------- queue / dialer ----------

export interface CallSummary {
  id: number;
  direction: 'outbound' | 'inbound';
  createdAt: ISODate;
  userName: string | null;
  campaignName: string | null;
  disposition: DispositionCode | null;
  durationSec: number | null;
  note: string | null;
  vmType: 'live' | 'drop' | null;
  /** PJ 2026-10-23 (migration 0029 calls.number_role): which of the lead's numbers the call went to, "Owner's line" or
   *  "Store line"; null/absent = the lead's one number, an inbound call, or a call before 0029. */
  numberRole?: DialTarget | null;
}

export interface QueueItem {
  membership: Membership;
  business: Business;
  campaign: Pick<Campaign, 'id' | 'name' | 'niche' | 'offer' | 'maxAttempts' | 'maxVms'>;
  reason: 'callback_due' | 'fresh' | 'retry';
  localTime: string;            // 'Tue 10:14 AM' prospect-local
  dialGate: GateResult;
  dropGate: GateResult;         // pre-computed for UI; re-checked server-side on drop
  history: CallSummary[];       // newest first, all campaigns
  openTasks: Task[];
  stateNote?: string | null;    // StateRule.setterNote for the prospect's state: shown above the script before dialing
  /** SPEC §19 (2026-10-02): a dial-pad call with "No campaign". There is no lead row: `membership` and `campaign` are
   *  placeholders with id 0, dial through POST /api/calls/prepare {noCampaign: true, businessId}. */
  noCampaign?: boolean;
  /** "Call back now" (2026-10-06): present while my own newest call to this number ended less than 15 minutes ago. */
  callBack?: CallBackOffer | null;
  /** PJ 2026-10-23: the lead's owner and store numbers (src/shared/api-numbers.ts), each with its own gate, the
   *  called-before lines and the owner's other stores. dialGate / dropGate are the default number's (numbers.defaultTarget). */
  numbers?: QueueItemNumbers;
}

/** "Call back now" (PJ 2026-10-06): redial the number just called, within 15 minutes, from the same caller ID.
 *  GET /api/calls/:id/call-back (the wrap-up, before the call is logged) and QueueItem.callBack (a lead reopened). */
export interface CallBackOffer {
  callId: number;               // the call it calls back (my newest placed call to this number)
  until: ISODate;               // 15 minutes after that call ended (or, end not known yet, after now)
  callerId: string | null;      // E.164 it redials from: the same number as that call
  gate: GateResult;             // the dial gate for the redial right now; refused = the sentences to show
  /** PJ 2026-10-23: which of the lead's numbers that call went to (the redial goes to the same one); null/absent = the
   *  lead's one number. Send it as CallPrepareRequest.target with quickCallback. */
  target?: DialTarget | null;
}

export interface QueueNextRequest { campaignId: number; skipMembershipIds?: number[] }
export interface QueueNextResponse { item: QueueItem | null; remainingEligible: number; blockedCounts: Partial<Record<GateReasonCode, number>>; handPickWaiting?: number /* leads in hand-pick-only states, open them from Leads */ }

export interface CallPrepareRequest {
  membershipId: number;
  // Lead engine (LEADGEN-SPEC §13 CORE 3): written to dial_signal_snapshots for calibration. Both optional.
  openerVariant?: string;      // the Script panel opener label the setter used, e.g. 'Default' | 'Observation'
  hookId?: number;             // lead_hooks.id shown in the drawer (DrawerData.hook.primary.id)
  /** Dial pad "Call from" (SPEC §19, 2026-10-02): one of our outbound numbers for this call only; omitted/null =
   *  automatic (the sticky / best local number). The server checks it is ours, outbound, ramping/active and under cap. */
  callFromNumberId?: number | null;
  /** "Call back now" (2026-10-06): redial my last call to this number (ended in the last 15 minutes) from its caller ID.
   *  Since PJ 2026-10-20 it is itself the setter's yes (it passes every soft attempt rule, never the 3-a-day cap). With
   *  noCampaign/businessId for a no-campaign call. */
  quickCallback?: boolean;
  /** PJ 2026-10-20: the setter said yes to "Call this number again?" (GateResult.confirm). It drops the soft attempt
   *  rules only; every other reason still blocks. With noCampaign/businessId for a no-campaign call. */
  redialConfirmed?: boolean;
  /** PJ 2026-10-28: the setter said yes to "Meeting booked. Call anyway?" (GateResult.confirm.meeting). The only yes
   *  that passes a meeting booked (a redialConfirmed alone never does: its card may predate the booking); it passes the
   *  soft attempt rules too. The dialer sends it with redialConfirmed. */
  meetingConfirmed?: boolean;
  /** PJ 2026-10-23: which of the lead's numbers to dial: 'owner' or 'store' (src/shared/api-numbers.ts). Absent = the
   *  default (the owner's number when there is one). The server derives the number from the business row, never from
   *  the client, and refuses a target the lead doesn't have (409 number_unavailable). With noCampaign/businessId too. */
  target?: DialTarget;
}
export interface CallPrepareResponse {
  callId: number;
  dialToken: string;           // pass to device.connect({ params: { callId, dialToken } })
  to: string;                  // E.164: the number this call dials (the owner's or the store's, PJ 2026-10-23)
  /** PJ 2026-10-23: the role of `to` among the lead's numbers; null/absent = the lead's one number. */
  numberRole?: DialTarget | null;
  callerId: string;            // E.164 of our number
  gate: GateResult;            // ok=true here; a blocked prepare returns 409 ApiError{error:'gate_blocked', details: GateResult}
                               // (details.confirm set when a yes would let it through: send redialConfirmed: true)
  /** PJ 2026-10-20: true only when the setting consent_recording_enabled is on and this call won't be recorded because
   *  everyone's consent is needed (a US all-party state, or a California or Washington area code). Once answered, the
   *  call bar offers "Record (they said yes)" -> POST /api/calls/:id/record-consent, which checks it all again. */
  consentRecording?: boolean;
}

export interface DropRequest { recordingId: number }
export interface DropResponse { ok: true; callId: number }

export interface DispositionRequest {
  code: DispositionCode;
  spokeToDm?: boolean;
  objection?: ObjectionCategory;
  note?: string;
  callbackAt?: ISODate;        // required for callback_set and dm_later
  dmName?: string;
  dmRole?: string;
  gatekeeperName?: string;
  bestTimeNote?: string;
  email?: string;              // captured on the call (for the invite)
  vmLive?: boolean;            // true when the setter read a live voicemail (code 'vm_live')
  /** No-campaign pad calls only, and required for them (SPEC §19, 2026-10-02): true = "Save to My leads",
   *  false = "Don't save" (the business is hidden; the call, outcome and any do-not-call request are kept). */
  saveLead?: boolean;
}
/** membership is null for a no-campaign pad call (it has no lead row). */
export interface DispositionResponse { membership: Membership | null; tasksCreated: Task[] }

// ---------- tasks & meetings ----------

export type TaskKind = 'callback' | 'follow_up' | 'email' | 'demo_build' | 'morning_text' | 'voicemail' | 'number_check' | 'other';

export interface Task {
  id: number;
  userId: number | null;
  businessId: number | null;
  businessName?: string | null;
  campaignId: number | null;
  membershipId: number | null;
  kind: TaskKind;
  dueAt: ISODate;
  title: string;
  note: string | null;
  doneAt: ISODate | null;
}

export interface Slot { startTime: ISODate; label: string } // label in the prospect's tz, e.g. 'Wed Sep 30, 2:00 PM EDT'

export interface MeetingBookRequest {
  membershipId: number;
  callId?: number;
  startTime: ISODate;          // must come from /api/meetings/slots
  name: string;
  email: string;
  mobile?: string;
  bookingSystem?: string;      // Calendly setter-link question 3 answer
  note?: string;
  textReminderOk?: boolean;
  eventSlug?: string;          // "Book into": the link chosen in the Book panel (omitted = the lead's default; api-core.ts)
}
export interface MeetingManualRequest { membershipId: number; callId?: number; scheduledFor: ISODate; name?: string; email?: string; note?: string; eventSlug?: string }

export type MeetingStatus = 'booked' | 'held' | 'no_show' | 'rescheduled' | 'canceled';
export interface Meeting {
  id: number;
  businessId: number;
  businessName: string;
  campaignId: number | null;
  setBy: number;
  setByName: string;
  scheduledFor: ISODate;
  inviteeName: string | null;
  inviteeEmail: string | null;
  status: MeetingStatus;
  cancelUrl: string | null;
  rescheduleUrl: string | null;
  outcomeNote: string | null;
  createdAt: ISODate;
}

// ---------- numbers ----------

export type NumberStatus = 'ramping' | 'active' | 'resting' | 'flagged' | 'retired';
export interface OurNumber {
  id: number;
  e164: string;
  country: Country;
  areaCode: string;
  region: string | null;
  metro: string | null;
  purpose: 'outbound' | 'main';
  status: NumberStatus;
  rampStartedAt: ISODate | null;
  dailyCap: number;            // computed from ramp: 20 / 40 / 60 (hard max 80)
  todayDials: number;
  cnamSet: boolean;
  fcrRegisteredAt: ISODate | null;
  hiyaRegisteredAt: ISODate | null;
  health: NumberHealth | null;
  lastAttestation?: { level: string; at: ISODate } | null; // from the T1 self-test call's StirVerstat (e.g. 'TN-Validation-Passed-A')
}

export interface NumberHealth {
  days: number;                // window used
  dials: number;
  answerRate: number | null;   // answered / dials
  connectRate: number | null;  // connects / dials
  shortCallRate: number | null;
  sip603: number;
  sip608: number;
  baselineConnectRate: number | null; // this number's own trailing 14 days before the window
  peerConnectRate: number | null;     // median of same-country active numbers
  alert: string | null;        // why it needs a check, or null
}

// ---------- voicemail recordings ----------

export interface VmRecording {
  id: number;
  userId: number;
  userName: string;
  campaignId: number | null;
  situation: string;
  durationSec: number | null;
  scriptText: string;
  humanVoiceAttested: boolean;
  status: 'pending' | 'approved' | 'rejected';
  reviewNote: string | null;
  playUrl: string;             // same-origin, authenticated
  createdAt: ISODate;
}

// ---------- state rules ----------

export interface CallingWindow {
  // prospect-local; days 1..6 = Mon..Sat, 0 = Sunday
  weekday: { start: string; end: string } | null;  // Mon-Fri
  saturday: { start: string; end: string } | null;
  /** SPEC §19 (owner decision 2026-10-03): US-only Sunday campaign hours. Absent or null = no Sunday calls (the
   *  default, and always for Canada). A state with `noSunday` never gets them. */
  sunday?: { start: string; end: string } | null;
}
export interface StateRule {
  code: string;                // 'US-GA'
  country: Country;
  name: string;
  callingAllowed: boolean;
  dropAllowed: boolean;
  window: CallingWindow | null;
  registrationNote: string | null;
  notes: string | null;
  sources: string[];
  reviewedAt: ISODate | null;
  scrubAllLines: boolean;      // national DNC scrub required for every line type in this state (e.g. GA, NV, NY)
  businessLinesOnly: boolean;  // dial only lookup-verified landline / fixedVoip numbers (e.g. NJ, TN)
  setterNote: string | null;   // one or two lines the setter sees on the lead card before dialing (state script duties)
  handPickOnly: boolean;       // never auto-queued: the setter opens each lead by hand (e.g. OK: 'automated selection OR dialing' law)
  /** Migration 0012: recording a call here needs every party's consent (CA, IL, PA, ...). Under recording_mode
   *  'one_party_only' calls with businesses in such a state are not recorded. Optional: absent = column not there yet. */
  allPartyConsent?: boolean;
  /** Migration 0021 (SPEC §19): a state law bans or restricts telemarketing on Sundays, so no Sunday calls here even
   *  when the US settings window has Sunday hours. Optional: absent = column not there yet (reads as false). */
  noSunday?: boolean;
}

// ---------- imports ----------

export interface ImportRowError { row: number; reason: string; raw: Record<string, string> }
export interface ImportReport {
  importId: number;
  total: number;
  imported: number;            // new memberships created
  reusedBusinesses: number;    // business already existed (history kept)
  rejected: ImportRowError[];
  flagged: Array<{ row: number; flag: string }>; // e.g. 'healthcare_ca', 'state_blocked', 'dnc_internal'
  lookupsQueued: number;       // US numbers queued for line-type lookup
}

// ---------- reports ----------

export interface FunnelRow {
  key: string;                 // grouping key, e.g. '2026-W40|12|3' (week|campaign|user)
  week: string;                // ISO week 'YYYY-Www'
  campaignId: number | null;
  campaignName: string | null;
  userId: number | null;
  userName: string | null;
  dials: number;
  connects: number;
  dmConversations: number;
  meetingsBooked: number;
  meetingsHeld: number;
  showRate: number | null;     // held / (held + no_show)
  dialsPerMeeting: number | null;
  connectRate: number | null;
  setRate: number | null;      // meetings booked / DM conversations
  vmLive: number;
  vmDropped: number;
  talkMinutes: number;
}

export interface HourBucket { localHour: number; dials: number; connects: number; meetings: number }

// ---------- training ----------

export type PersonaKind = 'gatekeeper' | 'lukewarm' | 'hostile';
export interface Persona { id: number; campaignId: number; kind: PersonaKind; name: string; brief: string; ready: boolean }

export interface ScorecardItem { key: string; label: string; score: number; max: number; evidence: string }
export interface Scorecard {
  total: number;               // 0-100
  passed: boolean;             // total >= 80
  items: ScorecardItem[];
  strengths: string[];
  fixes: string[];             // max 3, concrete
  model: string;
}

export interface PracticeSession {
  id: number;
  userId: number;
  campaignId: number;
  personaId: number;
  personaKind: PersonaKind;
  startedAt: ISODate;
  durationSec: number | null;
  score: number | null;
  passed: boolean | null;
  scorecard: Scorecard | null;
}

export interface PracticeStartResponse { sessionId: number; accessToken: string; callId: string } // Retell web call

export interface Certification {
  campaignId: number;
  userId: number;
  certified: boolean;
  passedSessions: number;      // within 30 days
  personaKindsPassed: PersonaKind[];
  required: { sessions: number; kinds: number }; // 3 sessions across >= 2 kinds
  exempt: boolean;
}

export interface TrainingItem {
  key: string;                 // 'd1-watch-30mpc-opener'
  day: number;                 // 1..10
  kind: 'watch' | 'read' | 'listen' | 'drill' | 'roleplay' | 'quiz' | 'shadow';
  title: string;
  url: string | null;          // verified resource URL
  minutes: number;
  why: string;
  doneAt: ISODate | null;
}
